/**
 * Mosaic advanced filter helpers — map UI state to Brooklyn `DocumentSemanticSearchFilter`
 * and `contentList` typeahead queries.
 */

export const FILTER_SCOPE_ANY = 'ANY'
export const FILTER_SCOPE_VIDEO = 'VIDEO'
export const FILTER_SCOPE_SERIES = 'SERIES'
export const FILTER_SCOPE_TOPIC = 'TOPIC'
export const FILTER_SCOPE_CENTER = 'CENTER'

export const FILTER_SCOPE_LABEL_KEYS = {
  [FILTER_SCOPE_ANY]: 'filterScopeAny',
  [FILTER_SCOPE_VIDEO]: 'filterScopeVideo',
  [FILTER_SCOPE_SERIES]: 'filterScopeSeries',
  [FILTER_SCOPE_TOPIC]: 'filterScopeTopic',
  [FILTER_SCOPE_CENTER]: 'filterScopeCenter',
}

/** Ordered enum values and the matching UI label key for the published age dropdown. */
export const PUBLISHED_AGE_OPTIONS = [
  { value: 'ANY', labelKey: 'filterPublishedAgeAny' },
  { value: 'PAST_MONTH', labelKey: 'filterPublishedAgePastMonth' },
  { value: 'PAST_3_MONTHS', labelKey: 'filterPublishedAgePast3Months' },
  { value: 'PAST_6_MONTHS', labelKey: 'filterPublishedAgePast6Months' },
  { value: 'PAST_YEAR', labelKey: 'filterPublishedAgePastYear' },
  { value: 'PAST_2_YEARS', labelKey: 'filterPublishedAgePast2Years' },
  { value: 'PAST_3_YEARS', labelKey: 'filterPublishedAgePast3Years' },
  { value: 'PAST_5_YEARS', labelKey: 'filterPublishedAgePast5Years' },
]

/** Max seasons to show in season dropdown (series rarely exceed 20). */
export const MAX_SEASONS = 20

/** Debounce delay (ms) for typeahead input. */
export const TYPEAHEAD_DEBOUNCE_MS = 300

/** Limit passed to contentList typeahead queries. */
export const TYPEAHEAD_LIMIT = 50

/**
 * True when the user picked a scope that requires a typeahead selection but has not chosen one.
 */
export function isFilterScopeIncomplete ({ filterScope, filterItem }) {
  return filterScope !== FILTER_SCOPE_ANY && filterItem == null
}

/**
 * True when the filter tray should show the active (highlighted) state — only when a filter
 * would actually be sent to Brooklyn.
 */
export function hasActiveFilterState ({
  filterScope, filterItem, filterPublishedAge,
}) {
  return (filterScope !== FILTER_SCOPE_ANY && filterItem != null)
    || filterPublishedAge !== 'ANY'
}

/**
 * Build the `$filter` object to pass in `DocumentSemanticSearchInput.filter`.
 * Returns null when no filter is active so the server takes Path 2 (HNSW).
 */
export function buildSearchFilter ({
  filterScope, filterItem, filterSeason, filterPublishedAge,
}) {
  const hasScope = filterScope !== FILTER_SCOPE_ANY && filterItem != null
  const hasDate = filterPublishedAge !== 'ANY'

  if (!hasScope && !hasDate) {
    return null
  }

  const base = hasDate ? { publishedAge: filterPublishedAge } : {}

  if (filterScope === FILTER_SCOPE_VIDEO) {
    return { ...base, contentId: filterItem.contentId }
  }
  if (filterScope === FILTER_SCOPE_SERIES) {
    return {
      ...base,
      seriesContentId: filterItem.contentId,
      seasonNumber: filterSeason != null ? filterSeason : undefined,
    }
  }
  if (filterScope === FILTER_SCOPE_TOPIC) {
    return { ...base, topicContentId: filterItem.contentId }
  }
  if (filterScope === FILTER_SCOPE_CENTER) {
    return { ...base, centerContentId: filterItem.contentId }
  }
  return hasDate ? base : null
}

/**
 * Build the ContentWhereInput used for typeahead lookups.
 */
export function buildTypeaheadWhere (scope, term) {
  const titleFilter = term ? { title: { ilike: `%${term.split(/\s+/u).join('%')}%` } } : { title: { ilike: '%' } }

  if (scope === FILTER_SCOPE_VIDEO) {
    return { ...titleFilter, contentType: { eq: 'video' } }
  }
  if (scope === FILTER_SCOPE_SERIES) {
    return { ...titleFilter, contentType: { eq: 'series' } }
  }
  if (scope === FILTER_SCOPE_TOPIC) {
    return { ...titleFilter, contentType: { eq: 'tag' }, filterType: { eq: 'conscious-topic' } }
  }
  if (scope === FILTER_SCOPE_CENTER) {
    return { ...titleFilter, contentType: { eq: 'tag' }, bundle: { eq: 'centers' } }
  }
  return null
}
